import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { DEFAULT_LOCALE } from './config';
import { SUPPORTED_CODES } from './languages';
import de from './locales/de.json';

/**
 * Namespaces — nach Code-Verantwortung geschnitten.
 * Die vier größten Dateien haben einen eigenen Namespace, damit Kataloge
 * überschaubar bleiben.
 */
export const NS = [
  'common',     // Buttons, Zustände, allgemeine Wörter
  'domain',     // Status/Priorität/SLA/Abteilung/Rolle — EINZIGE Quelle
  'shell',      // AppShell (Navigation, Ansichten)
  'ticket',     // TicketDetail
  'ticketList', // TicketList + Dialoge
  'settings',   // SettingsPage + Sections
  'views',      // Custom-View-Dialoge
  'dashboard',  // Dashboard, Home, Neuigkeiten
  'agent',      // KI-Entwürfe, Agent-Fälle, KI-Panel
  'auth',       // Login/Register/Passwort
  'warehouse',  // Lager-Oberfläche
  'errors',     // Service-/Hook-Meldungen,
  'admin',
] as const;

export type Namespace = (typeof NS)[number];

/**
 * Nur Deutsch liegt STATISCH im Bundle → kein Await, kein Flash für den
 * Standardfall. Alle anderen Sprachen werden als eigener Chunk nachgeladen.
 */
const loaders: Record<string, () => Promise<{ default: Record<string, any> }>> = {
  en: () => import('./locales/en.json'),
  tr: () => import('./locales/tr.json'),
  fr: () => import('./locales/fr.json'),
  es: () => import('./locales/es.json'),
  it: () => import('./locales/it.json'),
  nl: () => import('./locales/nl.json'),
  pl: () => import('./locales/pl.json'),
  pt: () => import('./locales/pt.json'),
};

i18n.use(initReactI18next).init({
  lng: DEFAULT_LOCALE,
  fallbackLng: DEFAULT_LOCALE,   // fehlender Schlüssel → Deutsch
  defaultNS: 'common',
  fallbackNS: 'common',
  ns: NS as unknown as string[],
  resources: { [DEFAULT_LOCALE]: de as Record<string, any> },
  supportedLngs: SUPPORTED_CODES,
  nonExplicitSupportedLngs: true, // 'tr-TR' → 'tr'
  keySeparator: '.',
  nsSeparator: ':',
  returnNull: false,
  returnEmptyString: false,       // leerer Wert → Fallback-Kette statt leerer UI
  saveMissing: false,
  interpolation: {
    escapeValue: false,           // PFLICHT: React escaped bereits (sonst "&amp;")
  },
  react: {
    useSuspense: false,           // App.tsx hat bereits ein eigenes <Suspense>
  },
  ...(import.meta.env.DEV
    ? {
        missingKeyHandler: (lngs: readonly string[], ns: string, key: string) => {
          // Sichtbar in der Entwicklung, still in Produktion (defaultValue greift ohnehin)
          console.warn(`[i18n] fehlender Schlüssel: ${ns}:${key} (${lngs.join(',')})`);
        },
      }
    : {}),
});

/** Sprachpaket nachladen (idempotent). Unbekannte Sprache → bleibt auf Deutsch. */
export async function loadLanguage(lng: string): Promise<boolean> {
  if (lng === DEFAULT_LOCALE) return true;
  if (i18n.hasResourceBundle(lng, 'common')) return true;

  const loader = loaders[lng];
  if (!loader) return false;

  const mod = await loader();
  const bundle = (mod.default ?? mod) as Record<string, any>;
  for (const ns of NS) {
    if (bundle[ns]) i18n.addResourceBundle(lng, ns, bundle[ns], true, true);
  }
  return true;
}

export default i18n;
